import React, { useRef, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToHorizontalAxis, restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import Header from "./Header/Header";
import Row from "./Row/Row";
import ErrorBoundary from "../../ErrorBoundary/ErrorBoundary"
import { connect } from "react-redux";
import { changeOrderTableWidth } from "../../../actions/ordersTable";
import { formatRowValue } from "../../../utils/mappers/ordersTable";
import { itemAccessors } from "../../../utils/constants";
import classNames from "classnames";
import "./Table.css";

const parsePseudoNull = (rowValue) => {
  if (rowValue === '-'){
    rowValue = 0;
  }
  return rowValue;
}

const calculateColumnSums = (rows, columns, decimalsNumber) => {
  const sums = columns.map(column => {
    const isNumericColumn = rows.every(row => !isNaN(parseFloat(parsePseudoNull(row.values[column.id]))));
    const excludedColumns = ['net_flow_priority', 'current_on_hand_alert', 'material_code', 'location', 'description', 'location_code_concatenation'];
    const isExcludedColumn = excludedColumns.includes(column.id);
    if (isNumericColumn && !isExcludedColumn) {
      var sumTotal = rows.reduce((sum, row) => {
        const formattedValue = formatRowValue(parsePseudoNull(row.values[column.id]));
        // Only add if it's a valid number, otherwise keep the current sum
        return (typeof formattedValue === 'number' && !isNaN(formattedValue)) ? sum + formattedValue : sum;
      }, 0);
      // Safety check before calling toFixed
      if (typeof sumTotal === 'number' && !isNaN(sumTotal)) {
        sumTotal = sumTotal.toFixed(2);
        return new Intl.NumberFormat("en-US", {minimumFractionDigits:decimalsNumber, maximumFractionDigits:decimalsNumber}).format(sumTotal);
      }
    }
    return null;
  });
  return sums;
};

const updateSubheaderPosition = (refContainer) => {
  const headerRow = refContainer.current.querySelector('.order-table-header');
  const subheaderCells = refContainer.current.querySelectorAll('.subheader th');
  if (headerRow && subheaderCells.length > 0) {
    const headerHeight = headerRow.offsetHeight;
    subheaderCells.forEach(cell => {
      cell.style.top = `${headerHeight}px`;
    });
  }
};

const Table = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  filterRows,
  rows,
  allRows,
  prepareRow,
  onSelectRow,
  editable,
  ordersCalculationId,
  selectedRow,
  setFilter,
  changeOrderTableWidth,
  onColumnReorder,
  rulesByItem,
  decimalsNumber
}) => {

  const refContainer = useRef(null);

  useEffect(() => {
    if (!refContainer.current) return;
    const resizeObserver = new ResizeObserver(() => {
      // max scroll left is like maxScrollLeft in firefox but this property does not exist in chrome
      var maxScrollLeft = refContainer.current.scrollWidth - refContainer.current.clientWidth;
      changeOrderTableWidth(maxScrollLeft);
      updateSubheaderPosition(refContainer);
    });
    resizeObserver.observe(refContainer.current);
    return () => resizeObserver.disconnect(); // clean up
  }, [changeOrderTableWidth]);

  useEffect(() => {
    if (!refContainer.current) return;
    updateSubheaderPosition(refContainer);
  }, [headerGroups]);

  const headersKey = headerGroups[0] ? headerGroups[0].headers.map(h => h.id).join(',') : '';
  const visibleColumnIds = headerGroups[0] ? headerGroups[0].headers.map(h => h.id) : [];

  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [activeDragWidth, setActiveDragWidth] = useState(null);
  const [activeDragHeight, setActiveDragHeight] = useState(null);
  const activeColumn = activeId && headerGroups[0] ? headerGroups[0].headers.find(h => h.id === activeId) : null;

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverlayVisible(false);
    requestAnimationFrame(() => {
      const rect = active.rect.current.initial || active.rect.current.translated;
      setActiveDragWidth(rect ? rect.width : null);
      setActiveDragHeight(rect ? rect.height : null);
      setOverlayVisible(true);
    });
  };

  const handleDragOver = ({ over }) => {
    setOverId(over ? over.id : null);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    setOverId(null);
    setOverlayVisible(false);
    setActiveDragWidth(null);
    setActiveDragHeight(null);
    if (over && active.id !== over.id) {
      onColumnReorder(active.id, over.id);
    }
  };

  // allRows keeps its identity until the data, filters or sorting change (not on pagination), so a recalculation or
  // an edit that changes values without changing the rows still refreshes the totals
  const columnSums = useMemo(
    () => calculateColumnSums(allRows, headerGroups[0].headers, decimalsNumber),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows, headersKey, decimalsNumber]
  );

  return (

    <div className="container data-table" id="container-data-table" ref={refContainer}>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis, restrictToFirstScrollableAncestor]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => { setActiveId(null); setOverId(null); }}
      >
      <ErrorBoundary>
        <table className="ui  celled table" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <React.Fragment key={headerGroup.id}>
                <SortableContext items={visibleColumnIds} strategy={horizontalListSortingStrategy}>
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const isStickyColumn = [itemAccessors.location, itemAccessors.code, itemAccessors.description].includes(column.id);
                      const isDropTarget = !isStickyColumn && column.id === overId && !!activeId && column.id !== activeId;
                      let dropSide = null;
                      if (isDropTarget) {
                        const activeIndex = visibleColumnIds.indexOf(activeId);
                        const overIndex = visibleColumnIds.indexOf(overId);
                        dropSide = activeIndex < overIndex ? "right" : "left";
                      }
                      return (
                        <Header
                          setFilter={setFilter}
                          tableWideFilteredRows={filterRows}
                          key={column.id}
                          column={column}
                          isSorted={column.isSorted}
                          isSortedDesc={column.isSortedDesc}
                          isDropTarget={isDropTarget}
                          dropSide={dropSide}
                        />
                      );
                    })}
                  </tr>
                </SortableContext>
                <tr className="subheader">
                  {headerGroup.headers.map((column, index) => (
                    <th
                      key={column.id}
                      style={{ textAlign: 'center' }}
                      className={classNames({
                        location: column.id === itemAccessors.location,
                        code: column.id === itemAccessors.code,
                        description: column.id === itemAccessors.description,
                      })}
                    >
                      {columnSums[index] !== null ? columnSums[index] : ''}
                    </th>
                  ))}
                </tr>
              </React.Fragment>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => (
              <Row
                ordersCalculationId={ordersCalculationId}
                key={row.id}
                prepareRow={prepareRow}
                onSelectRow={onSelectRow}
                editable={editable}
                row={row}
                selected={selectedRow && selectedRow.id === row.id}
                rulesByItem={rulesByItem}
              />
            ))}
          </tbody>
        </table>
      </ErrorBoundary>
      <DragOverlay>
        {activeColumn && overlayVisible ? (
          <div className="column-drag-overlay" style={{ width: activeDragWidth ?? undefined, height: activeDragHeight ?? undefined }}>
            {activeColumn.render("Header")}
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>
    </div >
  );
};

Table.propTypes = {
  onSelectRow: PropTypes.func,
  editable: PropTypes.bool,
  getTableProps: PropTypes.func,
  getTableBodyProps: PropTypes.func,
  headerGroups: PropTypes.array,
  filterRows: PropTypes.array,
  rows: PropTypes.array,
  allRows: PropTypes.array,
  prepareRow: PropTypes.func,
  ordersCalculationId: PropTypes.number,
  selectedRow: PropTypes.object,
  setFilter: PropTypes.func,
  changeOrderTableWidth: PropTypes.func,
  onColumnReorder: PropTypes.func,
  rulesByItem: PropTypes.bool,
  decimalsNumber: PropTypes.number,
};

Table.defaultProps = {
  onSelectRow: _.noop,
  editable: true,
};

// export default Table;
export default connect(null, { changeOrderTableWidth })(Table);
